import { Api } from '@/Api';
import { defineStore } from 'pinia';

export const PRO_STRING_PREFIX = 'pro';
export const DISCOUNT_MAX_SEATS = 3;

export interface OrganizationState {
  id?: string;
  name?: string;
  links: {
    invitationUrl?: string;
    invoicesUrl?: string;
  };
  free?: boolean;
  tier?: string;
  membersCount: number;
  seatsCount: number;
  seatsLeft: number;
  trial?: boolean;
  isPaying?: boolean;
  planCode?: string;
  versioningEnabled?: boolean;
  customStorage?: boolean;
  isNewPlanFor2025?: boolean;
  ownerId?: string;
  logo?: string;
  hideCloudappBranding?: boolean;
  redirectRootUrl?: string;
  preferences: {
    itemsRetention?: number;
    itemsRemoveAction?: string;
    itemsRemoveEnabledAt?: string;
    webhookUrl?: string;
  };
  requestContentSettings: {
    logCollectionDomain?: string;
    logCollectionDomains?: string[];
    defaultTitle?: string;
    defaultDescription?: string;
    endDescription?: string;
    contentTypeDefaults?: Record<string, { title?: string; description?: string }>;
  };
}

export const useOrganizationStore = defineStore('organizationStore', {
  state: () =>
    ({
      id: undefined,
      name: undefined,
      free: undefined,
      tier: undefined,
      membersCount: 0,
      seatsCount: 0,
      seatsLeft: 0,
      trial: undefined,
      isPaying: undefined,
      planCode: undefined,
      versioningEnabled: undefined,
      customStorage: undefined,
      isNewPlanFor2025: undefined,
      ownerId: undefined,
      logo: undefined,
      hideCloudappBranding: undefined,
      redirectRootUrl: undefined,
      links: {
        invitationUrl: undefined,
        invoicesUrl: undefined,
      },
      preferences: {
        itemsRetention: undefined,
        itemsRemoveAction: undefined,
        itemsRemoveEnabledAt: undefined,
        webhookUrl: undefined,
      },
      requestContentSettings: {
        logCollectionDomain: undefined,
        logCollectionDomains: [],
        defaultTitle: undefined,
        defaultDescription: undefined,
        endDescription: undefined,
        contentTypeDefaults: {},
      },
    }) as OrganizationState,
  actions: {
    initialize(orgData: any) {
      const membersCount = orgData.data.relationships.members.data.length;
      const attrs = orgData.data.attributes;

      this.$patch({
        id: orgData.data.id,
        links: {
          ...orgData.data.links,
        },
        ...attrs,
        membersCount,
        requestContentSettings: {
          // Backend sends in camelCase (logCollectionDomain) not snake_case (log_collection_domain)
          logCollectionDomain: orgData.data.attributes.logCollectionDomain || orgData.data.attributes.log_collection_domain,
          logCollectionDomains: orgData.data.attributes.logCollectionDomains || orgData.data.attributes.log_collection_domains || [],
          defaultTitle: orgData.data.attributes.requestVideoDefaultTitle || orgData.data.attributes.request_video_default_title,
          defaultDescription: orgData.data.attributes.requestVideoDefaultDescription || orgData.data.attributes.request_video_default_description,
          endDescription: orgData.data.attributes.requestVideoEndDescription || orgData.data.attributes.request_video_end_description,
          contentTypeDefaults: orgData.data.attributes.requestContentTypeDefaults || orgData.data.attributes.request_content_type_defaults || {},
        },
      });

      // change-case v4 preserves underscores before digit sequences, so
      // is_new_plan_for_2025 camelCases to isNewPlanFor_2025 (not isNewPlanFor2025)
      this.isNewPlanFor2025 = (attrs as any).isNewPlanFor_2025 ?? false;

      if (orgData.data?.relationships?.owner) {
        this.ownerId = orgData.data.relationships.owner.data.id;
      }

      return orgData;
    },
    sendOrgInvitations(emails: string[], source: string = 'invite-collaborators') {
      if (!this.id) {
        return Promise.reject('No organization id set');
      }

      return Api.organization.inviteMembers(this.id, emails, source);
    },
    async updateWebhookUrl(webhookUrl: string) {
      if (!this.id) {
        throw new Error('No organization id set');
      }

      await Api.organization.update(this.id, {
        webhook_url: webhookUrl,
      });

      this.$patch({
        preferences: {
          ...this.preferences,
          webhookUrl: webhookUrl,
        },
      });
    },
  },
  getters: {
    showMemberDiscount: (state: OrganizationState) => state.planCode?.includes(PRO_STRING_PREFIX) && state.membersCount < DISCOUNT_MAX_SEATS,
    invitationUrl: (state: OrganizationState) => state.links.invitationUrl,
    itemsRemoveEnabledAt: (state: OrganizationState) => state.preferences.itemsRemoveEnabledAt,
    itemsRetentionHumanReadable: (state: OrganizationState) => {
      if (!state.preferences.itemsRetention) {
        return '';
      }

      const { itemsRetention } = state.preferences;
      // intervals used in settings
      const intervals = [
        { seconds: 60 * 60, label: '1 hour' },
        { seconds: 60 * 60 * 24, label: '1 day' },
        { seconds: 60 * 60 * 24 * 7, label: '1 week' },
        { seconds: 60 * 60 * 24 * 30, label: '1 month' },
        { seconds: 60 * 60 * 24 * 90, label: '3 months' },
        { seconds: 60 * 60 * 24 * 180, label: '6 months' },
      ];

      const interval = intervals.find(i => i.seconds === itemsRetention);
      return interval ? interval.label : `${itemsRetention} seconds`;
    },
    itemsRemoveAction: (state: OrganizationState) => state.preferences.itemsRemoveAction,
    requestContentLogEnabled: (state: OrganizationState) => Boolean(state.requestContentSettings?.logCollectionDomain),
    requestContentLogDomain: (state: OrganizationState) => state.requestContentSettings?.logCollectionDomain || 'example.com',
  },
});
